// Ипортируем зависимости
import Vue from 'vue';
import Meta from 'vue-meta';
import { sync } from 'vuex-router-sync';
import App from './App.vue';
import { createMainStore, MODULES as STORE_MODULES } from './store';
import { MUTATIONS as USER_MUTATIONS } from './store/user';
import { createRouter } from './router';
import config from './config';
import apiConfig from './config/http/api.js';
import { createHttp } from './remote/index.js';
import { requestSSRLogger, responseSSRLogger } from './tools/axioslogger';
import {
  set$axiosInstance,
  set$store,
} from './tools/declare/global/initialGlobalModules'; // не удалять, инициализация глобал. мод

import * as filters from './filters';
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import Repo from "./services/modulebasket/repo";
import { NAME } from "./services";
import '../public/bvi/css/bvi.css';

let refreshReques = null;

Vue.use(VueToast);

// Добавляем возможность установки meta-тегов
Vue.use(Meta);

// Регистрируем глобальные фильтры
Object.keys(filters).forEach((key) => {
  Vue.filter(key, filters[key]);
});

/**
 * Экспортируем функцию (Factory), которая возвращает новый экземпляр приложения
 * со свежими настройками роутера (Router), хранилища (Store), http-клиента,
 * которое будет создаваться каждый раз заново при серверном запросе (SSR)
 */
export const createApp = (injections = {}, params = {}) => {
  // Провайдеры начинаются с $
  const providers = Object.keys(injections).reduce((res, key) => {
    if (injections.hasOwnProperty(key)) {
      res[`$${key}`] = injections[key];
    }
    return res;
  }, {});

  providers.$config = config;
  providers.$axiosReqResLoggers = {
    responseSSRLogger,
    requestSSRLogger,
  };

  // Настраиваем API-клиент
  const $api = createHttp(injections.isSSR, apiConfig);

  if ($api) {
    // Внедряем API в провайдеры
    providers.$api = $api;
    set$axiosInstance($api);
    if (providers.$captureError) {
      $api.interceptors.request.use(
        (req) => req,
        (err) => {
          providers.$captureError(err);
          return Promise.reject(err);
        }
      );
    }

    const { $session, $cookie } = providers;

    // Настраиваем сервис вторизации
    const $auth = {
      get tokens() {
        return $session && $session.load(config.authlocalStorageKey);
      },

      get authLabelFromCookie() {
        return $cookie && $cookie.load(config.authCookieKey);
      },

      signin({ token, refresh_token }) {
        if ($session) {
          $session.save(
            {
              token,
              refresh_token,
            },
            config.authlocalStorageKey
          );

          if ($cookie) {
            $cookie.save(config.authCookieKey, !!token, {
              path: '/',
              expires: 86400,
            });
          }
          store.commit('setIsTokensExist', true);
        }
      },

      signout() {
        if ($session) {
          let storage;
          try {
            storage = localStorage;
            const repo = new Repo(NAME, storage);
            repo.clear();
          } catch (e) {}
          $session.remove(config.authlocalStorageKey);
          if ($cookie) {
            $cookie.remove(config.authCookieKey);
          }
          store.commit('setIsTokensExist', false);
        }
      },
    };

    if ($auth) {
      $api.interceptors.request.use(
        (req) => {
          if (req.method === 'get') {
            // Добавим случайный параметр
            const key = new Date().getTime();
            req.params = req.params || {};
            //req.params.__nocache__ = [key, Math.random()].join('');
          }

          if ($auth.tokens) {
            req.headers.common[config.httpAuthHeader] = $auth.tokens.token;
          }

          if (injections.isSSR) {
            requestSSRLogger.writeLogs(req);
          }

          return req;
        },

        (err) => Promise.reject(err)
      );

      $api.interceptors.response.use(
        (res) => {
          if (injections.isSSR) {
            responseSSRLogger.writeLogs(res);
          }

          const { data } = res.data;
          refreshReques = null;

          if (data && data.token && data.refresh_token && data.expired_at) {
            $auth.signin(data);
          }

          return res;
        },
        async (err) => {
          if (!err.response && err.request) {
            return Promise.reject(err);
          }
          if (err.response.status === 401 && !$auth.tokens) {
            store.commit('user/clearProfile');
          }

          if (
            !$auth.tokens ||
            err.response.status !== 401 ||
            err.config.retry
          ) {
            return Promise.reject(err);
          }

          if (
            err.response.status === 401 &&
            err.config.data &&
            err.config.method === 'post'
          ) {
            store.commit('user/clearProfile');
            return Promise.reject(err);
          }

          if (!refreshReques) {
            refreshReques = store.dispatch(
              'user/refreshToken',
              $auth.tokens.refresh_token
            );
          }

          const {
            data: {
              data: { token },
            },
          } = await refreshReques;

          const newRequest = {
            ...err.config,
            headers: {
              ...err.config.headers,
              [config.httpAuthHeader]: token,
            },
            baseURL: err.config.baseURL !== '/api/' ? err.config.baseURL : '',
            retry: true,
          };

          return $api(newRequest);
        }
      );

      providers.$auth = $auth;
    }
  }

  // Создаем экземпляр хранилища
  const store = createMainStore(providers);

  // Создаем экземпляр роутера и синхронизируем его с хранилищем
  const router = createRouter(providers, store);
  sync(store, router);

  // Logout
  store.subscribe((mutation) => {
    // Подписываемся на мутацию очистки профайла
    if (
      mutation.type === `${STORE_MODULES.USER}/${USER_MUTATIONS.CLEAR_PROFILE}`
    ) {
      if (providers.$auth) {
        providers.$auth.signout();
      }
    }
  });

  /**
   * Создаем экземпляр приложения, а так же и внедряем в него:
   * - роутер (будет доступен как this.$router)
   * - хранилище (будет достуно как this.$store)
   */
  const app = new Vue({
    ...params,
    router,
    store,
    render: (h) => h(App),
    provide: providers,
  });

  // Блок регистрации локальных хранилищ
  let registered = {};

  app.isModuleRegistered = (name) => registered[name];

  app.registerModule = (
    name,
    registrator,
    store,
    providers,
    preserveState = false
  ) => {
    if (!app.isModuleRegistered(name)) {
      registered[name] = true;
      store.registerModule(name, registrator(providers), {
        preserveState,
      });
    }
  };

  app.unregisterModule = (name) => {
    if (!app.isModuleRegistered(name)) return;
    try {
      app.$store.unregisterModule(name);
      registered[name] = false;
    } catch (e) {
      // Тут можно выводить ошибку
    }
  };

  app.registerAsyncData = (list, store, route, providers) => {
    return list.map((item) => {
      const { options } = item;
      const { storeName, storeRegister } = options || item;
      if (storeName && storeRegister)
        app.registerModule(storeName, storeRegister, store, providers);

      let { asyncData } = item;
      if (!asyncData && options) {
        asyncData = options.asyncData;
        if (!asyncData && options.methods) {
          asyncData = options.methods.asyncData;
        }
      }

      if (!asyncData) return Promise.resolve();
      return asyncData({
        app,
        store,
        route,
        router,
        providers,
      });
    });
  };
  set$store(store); // создаем ссылку на вьюикс
  /**
   * Экспортируем приложение с внедренными плагинами
   * В этой точке приложение еще не монтируется, поскольку запуск
   * зависит от варианта обращения (браузер или SSR)
   */
  return {
    app,
    router,
    store,
    providers,
  };
};



// WEBPACK FOOTER //
// ./src/app.js